import { SimpleCustomerDto } from '../../../api/persons/dto/simple-customer.dto'
import { Modal, message } from 'antd'
import ClientSelect from '../../../../components/ClientSelect'
import { Typography, Button } from '@sureco/design-system'
import React, { useEffect, useRef, useState } from 'react'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import Papa from 'papaparse'
import ConfirmTerminationsModal from './TerminationConfirmationModal' // make sure to create this
import {
  createTerminationFileFromS3,
  getPresignedUploadUrl,
  uploadTerminationFile,
} from '../api/terminations-enrollment-platform-api-client'

function TerminationUploadModal({
  open,
  onOk,
  onCancel,
}: {
  open: boolean
  onOk: (FormData) => void
  onCancel: () => void
}) {
  const { clients } = useCensusV2Clients()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [selectedClient, setSelectedClient] = useState<
    SimpleCustomerDto | undefined
  >()
  const [previewVisible, setPreviewVisible] = useState(false)
  const [parsedRows, setParsedRows] = useState<any[]>([])
  const [uploading, setUploading] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    setSelectedFile(null)
    setPreviewVisible(false)
    setParsedRows([])
  }, [open])

  const handleCompanyChange = (value: string) => {
    const c = clients.find((c) => c.id === value)
    setSelectedClient(c)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const openFileDialog = () => {
    fileInputRef.current?.click()
  }

  const handleUpload = async () => {
    if (!selectedFile || !selectedClient) {
      message.error('Please select a file and a client before uploading.')
      return
    }

    // Parse CSV
    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          message.error('CSV file is empty or invalid.')
          return
        }
        setParsedRows(results.data)
        setPreviewVisible(true)
      },
      error: (err) => {
        message.error(`Failed to parse CSV: ${err.message}`)
      },
    })
  }

  const handleConfirm = async () => {
    setUploading(true)
    if (!selectedFile || !selectedClient) {
      message.error('Please select a file and a client before uploading.')
      return
    }
    try {
      const { name: fileName } = selectedFile
      const { presignedUploadUrl, importPublicKey } =
        await getPresignedUploadUrl({
          clientPublicKey: selectedClient?.id,
          fileName,
        })
      if (!presignedUploadUrl) {
        console.error('unable to retrieve presigned upload url')
        message.error('An error occurred. Please try again.')
        return
      }
      const uploadSuccess = await uploadTerminationFile(
        presignedUploadUrl,
        selectedFile
      )
      if (!uploadSuccess) {
        console.error('unable to uploadfile using presigned upload url')
        message.error('An error occurred. Please try again.')
        return
      }
      await createTerminationFileFromS3({
        fileName,
        importPublicKey,
        clientPublicKey: selectedClient?.id,
      })
      setSelectedFile(null)
      setUploading(false)
      setPreviewVisible(false)
      onOk(true)
    } catch (error) {
      console.error(error)
      message.error(`Failed to upload file. Error: ${error}`)
    }
  }

  const handleCancelPreview = () => {
    setPreviewVisible(false)
  }

  return (
    <>
      <Modal
        title="Upload Bulk Terminations"
        open={open}
        onOk={handleUpload}
        onCancel={onCancel}
        okText="Upload CSV"
      >
        <div>
          <Typography size={14}>Company:</Typography>
          <ClientSelect
            companies={clients.map(({ name, id }) => ({
              companyName: name,
              id: id,
            }))}
            defaultCompanyId={selectedClient?.id}
            onChange={handleCompanyChange}
          />
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <Button style={{ marginTop: '1em' }} onClick={openFileDialog}>
          Select File
        </Button>
        {selectedFile && <p>Selected File: {selectedFile.name}</p>}
      </Modal>

      <ConfirmTerminationsModal
        visible={previewVisible}
        onCancel={handleCancelPreview}
        onConfirm={handleConfirm}
        loading={uploading}
        terminationRows={parsedRows}
      />
    </>
  )
}

export default TerminationUploadModal
